import { Link } from 'react-router-dom';
import type { Order } from '@/services/orders';
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge';

interface OrdersTableProps {
  readonly orders: readonly Order[];
}

export const OrdersTable = ({ orders }: OrdersTableProps) => {
  if (orders.length === 0) {
    return (
      <div
        className="text-center"
        style={{
          padding: 'var(--space-12)',
          color: 'var(--text-tertiary)',
          fontSize: 'var(--text-sm)',
        }}
      >
        No orders found
      </div>
    );
  }

  return (
    <>
      {/* Mobile card view */}
      <div className="mobile-card-list">
        {orders.map((order) => (
          <Link
            key={order.orderId}
            to={`/admin/orders/${order.orderId}`}
            style={{
              display: 'block',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              padding: 'var(--space-3) var(--space-4)',
              textDecoration: 'none',
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-2)' }}>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-accent)',
                  fontFamily: 'var(--font-mono, monospace)',
                }}
              >
                #{order.orderId.slice(-8).toUpperCase()}
              </span>
              <OrderStatusBadge status={order.status} />
            </div>
            <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-1)' }}>
              <MobileCustomerName order={order} />
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                ${order.totalPrice.toFixed(2)}
              </span>
            </div>
            <div
              className="flex items-center"
              style={{
                gap: 'var(--space-2)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
              }}
            >
              <span>{order.type === 'quote' ? 'Custom' : 'Catalog'}</span>
              <MaterialCell order={order} />
              <span style={{ marginLeft: 'auto' }}>{formatRelativeDate(order.createdAt)}</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop table view */}
      <div
        className="overflow-hidden desktop-table"
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        <div className="table-scroll">
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px' }}>
            <thead style={{ backgroundColor: 'var(--bg-surface-sunken)' }}>
              <tr>
                {['Order', 'Customer', 'Material', 'Total', 'Status', 'Date'].map((heading) => (
                  <th
                    key={heading}
                    style={{
                      padding: 'var(--space-2) var(--space-4)',
                      textAlign: 'left',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'var(--text-tertiary)',
                      borderBottom: '1px solid var(--border-default)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr
                  key={order.orderId}
                  className="admin-table-row"
                  style={{
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <Link
                      to={`/admin/orders/${order.orderId}`}
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-accent)',
                        textDecoration: 'none',
                        fontFamily: 'var(--font-mono, monospace)',
                        letterSpacing: '0.02em',
                      }}
                    >
                      #{order.orderId.slice(-8).toUpperCase()}
                    </Link>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.7rem',
                        color: 'var(--text-tertiary)',
                        marginTop: '1px',
                      }}
                    >
                      {order.type === 'quote' ? 'Custom' : 'Catalog'}
                    </span>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <CustomerCell order={order} />
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <MaterialCell order={order} />
                  </td>
                  <td
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ${order.totalPrice.toFixed(2)}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <OrderStatusBadge status={order.status} />
                  </td>
                  <td
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatRelativeDate(order.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ---- Cell components ----

const MobileCustomerName = ({ order }: { order: Order }) => {
  const name = order.customerName ?? order.customerEmail;
  return (
    <span
      style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--text-primary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '60%',
      }}
    >
      {name ?? order.userId.slice(0, 10)}
    </span>
  );
};

const CustomerCell = ({ order }: { order: Order }) => {
  const name = order.customerName ?? order.customerEmail;
  if (!name) {
    return (
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono, monospace)',
        }}
      >
        {order.userId.slice(0, 10)}
      </span>
    );
  }

  return (
    <div>
      <p
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
        }}
      >
        {order.customerName ?? order.customerEmail}
      </p>
      {order.customerName && order.customerEmail && (
        <p style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
          {order.customerEmail}
        </p>
      )}
    </div>
  );
};

const MaterialCell = ({ order }: { order: Order }) => {
  const materialId = order.materialId ?? order.printJob?.materialId;
  const color = order.color ?? order.printJob?.color;

  if (!materialId && !color) {
    return (
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>--</span>
    );
  }

  return (
    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
      {materialId?.toUpperCase() ?? ''}
      {materialId && color ? ' / ' : ''}
      <span style={{ textTransform: 'capitalize' }}>{color ?? ''}</span>
    </span>
  );
};

// ---- Helpers ----

const formatRelativeDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
